<script setup>
import { computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import ConversationCard from 'dashboard/components/widgets/conversation/ConversationCard.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import EventSidebarSection from './EventSidebarSection.vue';

const props = defineProps({
  contactId: { type: Number, default: null },
  flush: { type: Boolean, default: false },
});

const store = useStore();
const { t } = useI18n();

const uiFlags = computed(
  () => store.getters['contactConversations/getUIFlags']
);

const conversations = computed(() => {
  if (!props.contactId) return [];
  const all = store.getters['contactConversations/getContactConversation'](
    props.contactId
  );
  return [...all]
    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
    .slice(0, 5);
});

const fetchConversations = contactId => {
  if (!contactId) return;
  const records = store.state.contactConversations?.records || {};
  const numericId = Number(contactId);
  if (records[numericId] !== undefined || records[contactId] !== undefined) {
    return;
  }
  store.dispatch('contactConversations/get', contactId);
};

onMounted(() => fetchConversations(props.contactId));

watch(
  () => props.contactId,
  (newId, oldId) => {
    if (newId && newId !== oldId) fetchConversations(newId);
  }
);
</script>

<template>
  <div
    v-if="contactId"
    :class="
      flush
        ? ''
        : 'rounded-xl border border-slate-100 p-5 shadow-sm dark:border-slate-900'
    "
  >
    <EventSidebarSection
      :title="t('PLANNING.SHOW.CONVERSATIONS_TITLE')"
      icon="i-lucide-message-square"
    >
      <div
        v-if="uiFlags.isFetching && conversations.length === 0"
        class="flex items-center justify-center py-6"
      >
        <Spinner />
      </div>

      <div v-else-if="conversations.length === 0" class="text-sm text-black">
        {{ t('PLANNING.SHOW.CONVERSATIONS_EMPTY') }}
      </div>

      <div v-else class="overflow-hidden">
        <ConversationCard
          v-for="conversation in conversations"
          :key="conversation.id"
          :chat="conversation"
          :hide-inbox-name="false"
          hide-thumbnail
          compact
          :enable-context-menu="false"
          class="dark:!border-slate-900 last:!border-b-0 rounded-lg"
        />
      </div>
    </EventSidebarSection>
  </div>
</template>
